import {
	Button,
	MenuItem,
	Typography,
	ListItemText,
	Menu, ListItemIcon
} from "@mui/material";
import {AgGridReact} from 'ag-grid-react';
import {useMemo, useEffect, useState} from 'react';
import {ColDef, RowSelectionModule} from "ag-grid-community";
import {useTheme} from '@mui/material/styles';
import {Box, Stack, TextField} from "@mui/material";
import type { RowSelectionOptions } from "ag-grid-community";
import { ModuleRegistry, TooltipModule, PinnedRowModule, CellStyleModule } from "ag-grid-community";
import PageLoading from "./PageLoading";
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CheckIcon from '@mui/icons-material/Check';

// AG Grid v33 needs each feature registered explicitly, and stays silent when one is
// missing: PinnedRowModule backs pinnedBottomRowData, CellStyleModule backs cellStyle/cellClass.
ModuleRegistry.registerModules([RowSelectionModule, TooltipModule, PinnedRowModule, CellStyleModule]);

export default function ServerSideDataTable(props: {
	columns: ColDef[],
	rows: any[],
	actions?: JSX.Element,
	noRowsText?: string,
	onSelectionChange?: (selected: any[]) => void
	rowSelection?: RowSelectionOptions,
	loading?: boolean,
	getRowHeight?: (params: any) => number,
	getRowId?: (params: any) => string,
	adminDomainSearch?: boolean
	columnLabels?: Record<string, string>,
	pinnedBottomRowData?: any[],
}) {
	const muiTheme = useTheme();	
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);
	const pageSizes = [10, 50, 100];

	// For switching themes.
	const [gridTheme, setGridTheme] = useState('ag-theme-alpine');

	// For filtering rows.
	const [filterText, setFilterText] = useState("");
	const [searchDomain, setSearchDomain] = useState<string>("");

	const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(
        props.columns.map(col => col.field || col.colId || "")
    );

    const toggleableColumns = useMemo(() => {
		return props.columns.filter(col => {
			const id = col.field || col.colId || "";
			return !!(props.columnLabels && id in props.columnLabels);
		});
	}, [props.columns, props.columnLabels]);

    const displayedColumns = useMemo(() => {
        return props.columns.filter(col => 
            visibleColumnIds.includes(col.field || col.colId || "")
        );
    }, [props.columns, visibleColumnIds]);

    const handleToggle = (id: string) => {
        setVisibleColumnIds(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };
	
	const defaultColDef = {
		flex: 1,
	};

	// Update AG Grid theme when MUI theme changes
	useEffect(() => {
		setGridTheme(muiTheme.palette.mode === 'dark'
			? 'ag-theme-alpine-dark'
			: 'ag-theme-alpine');
	}, [muiTheme.palette.mode]);

	// Filter logic (Domain search)
	const filteredUsers = useMemo(() => {
		return props.rows.filter((user) => {
			if (!searchDomain.trim()) return true;
			if (!user.connected_domain_names) return false;
			return user.connected_domain_names.some((domain: string) =>
			domain.toLowerCase().includes(searchDomain.toLowerCase())
			);
		});
	}, [props.rows, searchDomain]);


	return (
		<Box
			className={gridTheme}
			sx={{
				height: "100%",
				width: '100%',
				'&.ag-theme-alpine': {
					'--ag-foreground-color': muiTheme.palette.text.primary,
					'--ag-background-color': muiTheme.palette.background.paper,
					'--ag-header-foreground-color': muiTheme.palette.text.primary,
					'--ag-header-background-color': muiTheme.palette.background.default,
					'--ag-row-hover-color': muiTheme.palette.action.hover,
				},
				'&.ag-theme-alpine-dark': {
					'--ag-foreground-color': muiTheme.palette.text.primary,
					'--ag-background-color': muiTheme.palette.background.paper,
					'--ag-header-foreground-color': muiTheme.palette.text.primary,
					'--ag-header-background-color': muiTheme.palette.background.default,
					'--ag-row-hover-color': muiTheme.palette.action.hover,
				},
				/* ---------------- Responsive Pagination Overrides ---------------- */
                '& .ag-paging-panel': {
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 1,
                    py: 1,
                    height: 'auto !important',
                    minHeight: '48px',
                },
                '& .ag-paging-row-summary-panel, & .ag-paging-page-summary-panel': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    margin: '0 4px',
                },
                // Hide row count range ("1 to 1 of 1") on extra small mobile screens to save space
                '@media (max-width: 420px)': {
                    '& .ag-paging-row-summary-panel': {
                        display: 'none',
                    },
                },
			}}
		>
			<Stack direction={"column"} spacing={2} sx={{height: "100%"}}>
				<Box display="flex" justifyContent="space-between" alignItems="center">					
					<Stack direction="row" spacing={2}>
                        {props.adminDomainSearch && (
                            <TextField label="Search by Domain" size="small" value={searchDomain} onChange={(e) => setSearchDomain(e.target.value)} />
                        )}
                        <TextField placeholder="Search in table..." value={filterText} size="small" onChange={e => setFilterText(e.target.value)} />
                                                
                        {props.columnLabels && (
                            <>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ViewColumnIcon />}
                                    onClick={(e) => setAnchorEl(e.currentTarget)}
                                    sx={{ 
                                        textTransform: 'none', 
                                        color: 'text.primary', 
                                        borderColor: 'divider',
                                        height: '40px'
                                    }}
                                >
                                    Customize Columns
                                </Button>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={openMenu}
                                    onClose={() => setAnchorEl(null)}
                                    PaperProps={{ sx: { width: 220, maxHeight: 400, mt: 1, boxShadow: 3 } }}
                                >
                                    <Typography variant="overline" sx={{ px: 2, fontWeight: 'bold', color: 'text.secondary' }}>
                                        Toggle Columns
                                    </Typography>
                                                                        
                                    {toggleableColumns.map((col) => {
                                        const id = col.field || col.colId || "";
                                        const label = props.columnLabels?.[id] || col.headerName;
                                        const isVisible = visibleColumnIds.includes(id);

                                        return (
                                            <MenuItem key={id} onClick={() => handleToggle(id)} sx={{ py: 0.5 }}>
                                                <ListItemIcon sx={{ minWidth: '32px !important' }}>
                                                    {isVisible && <CheckIcon fontSize="small" sx={{ fontSize: 16 }} />}
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={label} 
                                                    primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }} 
                                                />
                                            </MenuItem>
                                        );
                                    })}
                                </Menu>
                            </>
                        )}
                    </Stack>
                    {props.actions}
                </Box>
				<AgGridReact
					loading={props.loading ?? false}
					rowData={filteredUsers}
					columnDefs={displayedColumns}
					defaultColDef={defaultColDef}
					pinnedBottomRowData={props.pinnedBottomRowData}
					tooltipShowDelay={0}
					tooltipHideDelay={2000}
					pagination={true}
					paginationPageSize={pageSizes[1]}
					paginationPageSizeSelector={pageSizes}
					quickFilterText={filterText}
					enableCellTextSelection={true}
					ensureDomOrder={true}
					overlayNoRowsTemplate={props.noRowsText || "No Rows Available"}
					loadingOverlayComponent={PageLoading}
					getRowId={props.getRowId}
					rowSelection={props.rowSelection}
					onSelectionChanged={(event) => {
						const selectedRows = event.api.getSelectedRows();
						props.onSelectionChange?.(selectedRows);
					}}
					getRowHeight={props.getRowHeight}
				/>
			</Stack>
		</Box>
	)
}
